/**
 * The default transform gizmo on the Object Editor's 3D model: move, rotate
 * and scale write `spec.model3d` (position / rotation / scale in the object's
 * authored frame). A drag is one editor transaction = one undo step.
 */

import * as THREE from 'three';
import type { ObjectModel3D, Vec3 } from 'shared-types';
import type { GizmoAxis, GizmoFrame, GizmoSpace, TransformGizmoBinding } from '../../../potree/personas/tools/modules/transformGizmo/TransformGizmoBinding';
import type { Snapshot } from '../../../potree/personas/tools/ToolUndoRedoManager';
import type { EditorStore } from '../document/EditorStore';

const AXIS_INDEX: Record<GizmoAxis, 0 | 1 | 2> = { x: 0, y: 1, z: 2 };

/** Snap reach: 4% of the object's largest dimension, at least 2 cm. */
const SNAP_FRACTION = 0.04;
const SNAP_MIN = 0.02;

/** The nearest target within `reach`, else the value unchanged. */
function snapTo(value: number, targets: readonly number[], reach: number): number {
  let best = value;
  let bestDistance = reach;
  for (const target of targets) {
    const distance = Math.abs(value - target);
    if (distance <= bestDistance) {
      best = target;
      bestDistance = distance;
    }
  }
  return best;
}

const round = (v: number): number => Math.round(v * 1e5) / 1e5;
const roundVec = (v: THREE.Vector3 | THREE.Euler): Vec3 => [round(v.x), round(v.y), round(v.z)];

export class ModelPlacementBinding implements TransformGizmoBinding {
  private base: ObjectModel3D | null = null;

  constructor(private readonly store: EditorStore) {}

  private get model(): ObjectModel3D | null {
    return this.store.getState().spec.model3d ?? null;
  }

  private update(next: ObjectModel3D): void {
    this.store.apply((spec) => ({ ...spec, model3d: next }));
  }

  getFrame(): GizmoFrame | null {
    const model = this.model;
    if (!model) return null;
    return {
      position: new THREE.Vector3(...model.position),
      quaternion: new THREE.Quaternion().setFromEuler(new THREE.Euler(...model.rotation, 'XYZ')),
    };
  }

  /** Object space: the scale handles are exactly the model's own scale axes. */
  getSpace(): GizmoSpace {
    return 'object';
  }

  getEnabledHandles(): ReadonlyArray<string> | null {
    return null;
  }

  beginDrag(): void {
    this.base = this.model;
    this.store.begin();
  }

  /**
   * Moves snap the pivot onto the footprint box when it comes close: its
   * edges and center line in plan, the floor and top in height (per axis, so
   * it can slide along an edge). Shift or Ctrl/Cmd = free move.
   */
  applyTranslation(worldDelta: THREE.Vector3, override: boolean): void {
    if (!this.base) return;
    const p = new THREE.Vector3(...this.base.position).add(worldDelta);
    if (!override) {
      const { width, depth, height } = this.store.getState().spec.defaultSize;
      const reach = Math.max(SNAP_MIN, SNAP_FRACTION * Math.max(width, depth, height));
      p.x = snapTo(p.x, [-width / 2, 0, width / 2], reach);
      p.y = snapTo(p.y, [-depth / 2, 0, depth / 2], reach);
      p.z = snapTo(p.z, [0, height], reach);
    }
    this.update({ ...this.base, position: roundVec(p) });
  }

  applyRotation(worldAxis: THREE.Vector3, angle: number): void {
    if (!this.base) return;
    const q = new THREE.Quaternion().setFromAxisAngle(worldAxis, angle).multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(...this.base.rotation, 'XYZ')));
    this.update({ ...this.base, rotation: roundVec(new THREE.Euler().setFromQuaternion(q, 'XYZ')) });
  }

  applyScale(axes: ReadonlyArray<GizmoAxis>, factor: number): void {
    if (!this.base || !(factor > 1e-6)) return;
    const scale: [number, number, number] = [...this.base.scale];
    for (const axis of axes) scale[AXIS_INDEX[axis]] = round(this.base.scale[AXIS_INDEX[axis]] * factor);
    this.update({ ...this.base, scale });
  }

  /** The editor store keeps the undo history: nothing for a tool undo stack. */
  commitDrag(): Snapshot | null {
    this.base = null;
    this.store.commit();
    return null;
  }

  cancelDrag(): void {
    this.base = null;
    this.store.rollback();
  }
}
